// src/pages/CategoryList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Snackbar, Alert, TextField,
  Pagination, Stack, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { getAllCategories, deleteCategory } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import CategoryFormDialog from './CategoryFormDialog';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';

function CategoryList() {
  // paginação
  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);

  // dados
  const [items, setItems] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [carregando, setCarregando] = useState(false);

  // busca
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  // ui
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [confirmItem, setConfirmItem] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const notificar = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('descricao', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await getAllCategories({ indice: pagina, qtde, termo, ordenar: orderBy, direcao: order });
      setItems(data.items ?? []);
      setTotalPaginas(data.totalPages ?? 1);
      setTotalItens(data.totalItems ?? 0);
    } catch (e) {
      notificar(getApiErrorMessage(e, 'Erro ao carregar categorias'), 'error');
    } finally {
      setCarregando(false);
    }
  }, [pagina, qtde, termo, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  const sorted = items;

  const onBuscar = () => { setPagina(1); setTermo(searchText.trim()); };
  const onKeyDown = (e) => { if (e.key === 'Enter') onBuscar(); };

  const abrirNova = () => { setEditing(null); setFormOpen(true); };
  const abrirEdicao = (categoria) => { setEditing(categoria); setFormOpen(true); };

  const aoSalvar = (msg) => {
    setFormOpen(false);
    setEditing(null);
    notificar(msg);
    carregar();
  };

  const excluir = async () => {
    setExcluindo(true);
    try {
      await deleteCategory(confirmItem.id);
      notificar('Categoria excluída');
      setConfirmItem(null);
      // se removemos o único item da página e há páginas anteriores, volta uma
      if (items.length === 1 && pagina > 1) setPagina(pagina - 1);
      else carregar();
    } catch (e) {
      setConfirmItem(null);
      notificar(getApiErrorMessage(e, 'Não foi possível excluir a categoria.'), 'error');
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Categorias"
        subtitle="Agrupamento principal dos produtos, equivalente ao grupo no ZenPDV."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
            Nova categoria
          </Button>
        }
      />

      {/* toolbar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por descrição..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={onKeyDown}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: !!searchText && (
                <IconButton size="small" onClick={() => { setSearchText(''); setTermo(''); setPagina(1); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }}
            sx={{ flexGrow: 1, minWidth: 240 }}
          />
          <Button variant="outlined" onClick={onBuscar}>Buscar</Button>
        </Stack>
      </Paper>

      {/* tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="Código" width={100} orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right" width={120}><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {termo ? `Nenhuma categoria encontrada para "${termo}"` : 'Nenhuma categoria cadastrada'}
                </TableCell>
              </TableRow>
            ) : sorted.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => abrirEdicao(c)}>
                <TableCell data-label="Código">{c.id}</TableCell>
                <TableCell data-label="Descrição">{c.descricao}</TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirEdicao(c)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => setConfirmItem(c)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* paginação */}
      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItens} categorias (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <CategoryFormDialog
        open={formOpen}
        category={editing}
        onClose={() => setFormOpen(false)}
        onSaved={aoSalvar}
      />

      {/* confirmação de exclusão */}
      <Dialog open={!!confirmItem} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir categoria</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Excluir <strong>{confirmItem?.descricao}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)} color="inherit" disabled={excluindo}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={excluir} disabled={excluindo}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CategoryList;
